import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLanguage } from '@/hooks/useLanguage';
import { sanitizeText, sanitizeEmail, sanitizePhone, checkRateLimit, clearRateLimit } from '@/lib/security';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Briefcase, 
  Scale, 
  HeartPulse, 
  Users, 
  Calculator, 
  FileText, 
  Home, 
  Building2 
} from 'lucide-react';

// Import step components
import ServiceSelectionStep from '@/components/booking/ServiceSelectionStep';
import BasicInfoStep from '@/components/booking/BasicInfoStep';
import ConditionalQuestionsStep from '@/components/booking/ConditionalQuestionsStep';
import AppointmentStep from '@/components/booking/AppointmentStep';
import ReferralStep from '@/components/booking/ReferralStep';
import PaymentStep from '@/components/booking/PaymentStep';
import ConsentStep from '@/components/booking/ConsentStep';

const services = [
  { id: 'Immigration', icon: Briefcase },
  { id: 'Criminal Defense', icon: Scale },
  { id: 'Personal Injury', icon: HeartPulse },
  { id: 'Family Law', icon: Users },
  { id: 'Tax', icon: Calculator },
  { id: 'Probate & Estate Planning', icon: FileText },
  { id: 'Real Estate', icon: Home },
  { id: 'Business Law', icon: Building2 },
];

const createFormSchema = (t: any) => z.object({
  service: z.string().min(1, t('booking.required')),
  fullName: z.string()
    .min(1, t('booking.required'))
    .max(100, "Name must be less than 100 characters.")
    .regex(/^[a-zA-ZÀ-ÿ\s\-']+$/, "Name contains invalid characters."),
  dateOfBirth: z.string().min(1, t('booking.required')),
  email: z.string()
    .email(t('booking.invalidEmail'))
    .max(255, "Email must be less than 255 characters.")
    .toLowerCase()
    .refine((email) => {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      return emailRegex.test(email);
    }, t('booking.invalidEmail')),
  phone: z.string()
    .min(10, t('booking.invalidPhone'))
    .max(20, "Phone number is too long.")
    .regex(/^[\d\s\-\(\)\+]+$/, "Phone number contains invalid characters."),
  address: z.string().max(500, "Address is too long.").optional(),
  employerTitle: z.string().max(200, "Title is too long.").optional(),
  situation: z.string()
    .min(30, t('booking.minLength').replace('{count}', '30'))
    .max(5000, "Description is too long.")
    .refine((msg) => {
      const sqlPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)|(-{2})/gi;
      return !sqlPattern.test(msg);
    }, "Description contains invalid content."),
  
  // Conditional fields
  immigrationData: z.any().optional(),
  criminalData: z.any().optional(),
  injuryData: z.any().optional(),
  familyData: z.any().optional(),
  taxData: z.any().optional(),
  probateData: z.any().optional(),
  realEstateData: z.any().optional(),
  businessData: z.any().optional(),
  
  // Appointment
  appointmentType: z.enum(['In-person', 'Phone', 'Video']),
  preferredDate: z.string().min(1, t('booking.required')), // REQUIRED field
  preferredTime: z.string().min(1, t('booking.required')),
  
  // Referral
  referredBy: z.string().min(1, t('booking.required')),
  referredOther: z.string().optional(),
  
  // Payment
  paymentMethod: z.enum(['Stripe', 'Zelle', 'Check', 'Wire']),
  zelleConfirmation: z.string().optional(),
  paymentEvidenceFile: z.any().optional(),
  
  // Consent
  consentNotifications: z.boolean().refine(val => val === true, t('booking.required')),
  consentTerms: z.boolean().refine(val => val === true, t('booking.required')),
  marketingConsent: z.boolean().optional(),
}).refine((data) => {
  // If payment method is Zelle, zelleConfirmation is required
  if (data.paymentMethod === 'Zelle') {
    return data.zelleConfirmation && data.zelleConfirmation.trim().length > 0;
  }
  return true;
}, {
  message: "Zelle confirmation number is required",
  path: ["zelleConfirmation"],
});

interface BookingFormProps {
  preselectedService?: string | null;
}

const BookingForm = ({ preselectedService }: BookingFormProps) => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isZelleValid, setIsZelleValid] = useState(true);
  const formTopRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-scroll to next button when user makes a selection
  const scrollToNextButton = () => {
    setTimeout(() => {
      nextButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };
  
  const totalSteps = 7;

  const form = useForm<z.infer<ReturnType<typeof createFormSchema>>>({
    resolver: zodResolver(createFormSchema(t)),
    defaultValues: {
      service: preselectedService || '',
      fullName: '',
      dateOfBirth: '',
      email: '',
      phone: '',
      address: '',
      employerTitle: '',
      situation: '',
      appointmentType: 'In-person',
      referredBy: '',
      referredOther: '',
      paymentMethod: 'Stripe',
      zelleConfirmation: '',
      consentNotifications: false,
      consentTerms: false,
      marketingConsent: false,
    },
  });

  // Save to localStorage on change
  useEffect(() => {
    const subscription = form.watch((value) => {
      localStorage.setItem('bookingFormData', JSON.stringify(value));
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('bookingFormData');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        Object.keys(data).forEach((key) => {
          form.setValue(key as any, data[key]);
        });
      } catch (e) {
        console.error('Failed to load saved form data', e);
      }
    }
  }, []);

  // Set preselected service if provided
  useEffect(() => {
    if (preselectedService) {
      form.setValue('service', preselectedService);
      if (currentStep === 1) {
        setCurrentStep(2);
      }
    }
  }, [preselectedService]);

  const onSubmit = async (data: z.infer<ReturnType<typeof createFormSchema>>) => {
    // Rate limiting check
    if (!checkRateLimit('booking_form', 2, 600000)) { // 2 intentos por 10 minutos
      toast({
        title: language === 'es' ? 'Demasiados Intentos' : 'Too Many Attempts',
        description: language === 'es' 
          ? 'Por favor espere unos minutos antes de intentar nuevamente.'
          : 'Please wait a few minutes before trying again.',
        variant: 'destructive',
      });
      return;
    }

    // Validate Zelle confirmation AND receipt before submission
    if (data.paymentMethod === 'Zelle') {
      const zelleConfirmation = data.zelleConfirmation;
      const paymentEvidenceFile = data.paymentEvidenceFile;
      
      const hasConfirmation = zelleConfirmation && zelleConfirmation.trim().length > 0;
      const hasReceipt = !!paymentEvidenceFile;
      
      if (!hasConfirmation || !hasReceipt) {
        toast({
          title: language === 'es' ? '⚠️ PROCESO INCOMPLETO' : '⚠️ INCOMPLETE PROCESS',
          description: language === 'es'
            ? 'Para continuar debe: 1) Completar el pago por Zelle, 2) Ingresar el número de confirmación, y 3) Subir el comprobante de pago.'
            : 'To continue you must: 1) Complete the Zelle payment, 2) Enter the confirmation number, and 3) Upload the payment receipt.',
          variant: 'destructive',
        });
        if (!hasConfirmation) {
          form.setError('zelleConfirmation', {
            type: 'manual',
            message: language === 'es' 
              ? 'El número de confirmación de Zelle es obligatorio'
              : 'Zelle confirmation number is required',
          });
        }
        setCurrentStep(6); // Return to payment step
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const bookingId = `BK-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Get user IP for audit
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      
      // Combine date and time into appointment_start timestamp
      const appointmentDateTime = new Date(`${data.preferredDate}T${data.preferredTime}`);
      const appointmentEnd = new Date(appointmentDateTime.getTime() + 60 * 60 * 1000);
      
      // Upload Zelle receipt to storage if present
      let paymentEvidenceUrl = null;
      if (data.paymentMethod === 'Zelle' && data.paymentEvidenceFile) {
        const file = data.paymentEvidenceFile;
        const fileExt = file.name.split('.').pop();
        const fileName = `${bookingId}-receipt.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('payment-receipts')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Error uploading receipt:', uploadError);
        } else {
          paymentEvidenceUrl = fileName; // Store just the filename, admins will fetch with signed URL
        }
      }
      
      // Determine payment status based on method
      const paymentStatus = data.paymentMethod === 'Zelle' ? 'pending_verification' : 'pending';
      
      const bookingData = {
        booking_id: bookingId,
        service: sanitizeText(data.service),
        full_name: sanitizeText(data.fullName),
        date_of_birth: data.dateOfBirth,
        email: sanitizeEmail(data.email),
        phone: sanitizePhone(data.phone),
        address: data.address ? sanitizeText(data.address) : null,
        employer_title: data.employerTitle ? sanitizeText(data.employerTitle) : null,
        situation: sanitizeText(data.situation),
        immigration_data: data.immigrationData || null,
        criminal_data: data.criminalData || null,
        injury_data: data.injuryData || null,
        family_data: data.familyData || null,
        tax_data: data.taxData || null,
        probate_data: data.probateData || null,
        real_estate_data: data.realEstateData || null,
        business_data: data.businessData || null,
        appointment_type: data.appointmentType,
        appointment_start: appointmentDateTime.toISOString(),
        appointment_end: appointmentEnd.toISOString(),
        referred_by: data.referredBy,
        referred_other: data.referredOther || null,
        payment_method: data.paymentMethod,
        payment_status: paymentStatus,
        payment_evidence_url: paymentEvidenceUrl,
        zelle_confirmation: data.paymentMethod === 'Zelle' ? data.zelleConfirmation : null,
        consent_notifications: data.consentNotifications,
        consent_non_refundable: data.consentTerms,
        consent_ip: ipData.ip,
        language: language,
      };

      const { error } = await supabase
        .from('bookings')
        .insert(bookingData);

      if (error) throw error;

      // Use local bookingData since unauthenticated users can't SELECT back
      const booking = { ...bookingData, booking_id: bookingId };

      // For non-Stripe payments, create Zoom meeting and Calendar event BEFORE sending emails
      let zoomData = null;
      let calendarEventId = null;

      if (data.paymentMethod !== 'Stripe') {
        try {
          // Create Zoom meeting for Video appointments
          if (data.appointmentType === 'Video') {
            console.log('[Booking] Creating Zoom meeting for Video appointment (Zelle/Check)...');
            const { data: zoomResponse, error: zoomError } = await supabase.functions.invoke('create-zoom-meeting', {
              body: {
                topic: `Legal Consultation - ${data.service}`,
                start_time: appointmentDateTime.toISOString(),
                duration: 60,
                timezone: 'America/Chicago',
                agenda: `Legal consultation with ${data.fullName} regarding ${data.service}`,
              },
            });

            if (zoomError) {
              console.error('[Booking] Error creating Zoom meeting:', zoomError);
            } else {
              zoomData = zoomResponse;
              console.log('[Booking] Zoom meeting created:', zoomData?.meeting_id);
            }
          }

          // Create Google Calendar event for ALL appointment types - THIS BLOCKS THE TIME SLOT
          console.log(`[Booking] Creating Google Calendar event to BLOCK time slot for ${data.appointmentType} appointment...`);
          
          let eventDescription = `📋 Booking ID: ${bookingId}\n\nService: ${data.service}\nClient: ${data.fullName}\nPhone: ${data.phone}\nEmail: ${data.email}\nType: ${data.appointmentType}\nPayment: ${data.paymentMethod}`;
          
          if (data.appointmentType === 'In-person') {
            eventDescription += '\n\n📍 Location: 1800 Bering Dr., Suite 1050, Houston, TX 77057';
          } else if (data.appointmentType === 'Phone') {
            eventDescription += `\n\n📞 Phone call scheduled. Call client at: ${data.phone}`;
          } else if (data.appointmentType === 'Video' && zoomData?.join_url) {
            eventDescription += `\n\n🎥 Zoom Link: ${zoomData.join_url}`;
            if (zoomData?.password) {
              eventDescription += `\nPassword: ${zoomData.password}`;
            }
          }
          
          const { data: calendarResponse, error: calendarError } = await supabase.functions.invoke('create-calendar-event', {
            body: {
              summary: `${data.appointmentType === 'Video' ? '🎥' : data.appointmentType === 'Phone' ? '📞' : '🏢'} Legal Consultation - ${data.fullName} (${data.service})`,
              description: eventDescription,
              start_time: appointmentDateTime.toISOString(),
              end_time: appointmentEnd.toISOString(),
              timezone: 'America/Chicago',
              attendee_email: data.email,
              attendee_name: data.fullName,
              zoom_join_url: zoomData?.join_url,
              booking_id: bookingId,
            },
          });

          if (calendarError) {
            console.error('[Booking] ❌ Error creating calendar event:', calendarError);
            // Don't fail the booking, but log the error
          } else if (calendarResponse?.success) {
            calendarEventId = calendarResponse.event_id;
            console.log('[Booking] ✅ Calendar event created and time slot BLOCKED:', calendarEventId);
          } else {
            console.error('[Booking] ⚠️ Calendar event creation returned unexpected response:', calendarResponse);
          }

          // Update booking with Zoom/Calendar data
          const updateData: any = {};
          if (zoomData) {
            updateData.zoom_meeting_id = zoomData.meeting_id;
            updateData.zoom_join_url = zoomData.join_url;
            updateData.zoom_password = zoomData.password;
            updateData.zoom_start_url = zoomData.start_url;
          }
          if (calendarEventId) {
            updateData.calendar_event_id = calendarEventId;
          }

          if (Object.keys(updateData).length > 0) {
            await supabase
              .from('bookings')
              .update(updateData)
              .eq('booking_id', bookingId);
          }
        } catch (integrationError) {
          console.error('[Booking] Error with Zoom/Calendar integration:', integrationError);
        }

        // Send confirmation email to client with Zoom link
        console.log('[Booking] 📧 Sending confirmation email to client:', data.email);
        try {
          const { data: emailResponse, error: emailError } = await supabase.functions.invoke('send-booking-confirmation', {
            body: {
              bookingId: booking.booking_id,
              email: data.email,
              fullName: data.fullName,
              service: data.service,
              appointmentType: data.appointmentType,
              appointmentStart: booking.appointment_start,
              paymentMethod: data.paymentMethod,
              language: language,
              zoomJoinUrl: zoomData?.join_url,
              zoomPassword: zoomData?.password,
              calendarEventId: calendarEventId,
            },
          });
          
          if (emailError) {
            console.error('[Booking] ❌ Error sending client confirmation email:', emailError);
            toast({
              title: language === 'es' ? 'Advertencia' : 'Warning',
              description: language === 'es' 
                ? 'La reserva fue creada pero hubo un problema enviando el correo de confirmación.'
                : 'Booking was created but there was a problem sending the confirmation email.',
              variant: 'destructive',
            });
          } else {
            console.log('[Booking] ✅ Client confirmation email sent successfully');
          }
        } catch (emailError) {
          console.error('[Booking] ❌ Exception sending confirmation email:', emailError);
        }

        // Send team notification with Zoom link
        console.log('[Booking] 📧 Sending notification to admin team...');
        try {
          const { error: notificationError } = await supabase.functions.invoke('notify-team-booking', {
            body: {
              bookingId: booking.booking_id,
              fullName: data.fullName,
              email: data.email,
              phone: data.phone,
              service: data.service,
              appointmentType: data.appointmentType,
              appointmentStart: booking.appointment_start,
              paymentMethod: data.paymentMethod,
              paymentStatus: bookingData.payment_status,
              situation: data.situation,
              language: language,
              zoomJoinUrl: zoomData?.join_url,
              zoomStartUrl: zoomData?.start_url,
              zoomPassword: zoomData?.password,
              calendarEventId: calendarEventId,
            },
          });
          
          if (notificationError) {
            console.error('[Booking] ❌ Error sending team notification:', notificationError);
          } else {
            console.log('[Booking] ✅ Team notification sent successfully');
          }
        } catch (notificationError) {
          console.error('[Booking] ❌ Exception sending team notification:', notificationError);
        }

        // Send Zelle verification notification to admin
        if (data.paymentMethod === 'Zelle') {
          try {
            const { error: zelleNotifyError } = await supabase.functions.invoke('notify-zelle-payment', {
              body: {
                bookingId: booking.booking_id,
                clientName: data.fullName,
                clientEmail: data.email,
                clientPhone: data.phone,
                service: data.service,
                amount: 15000, // $150.00 in cents
                zelleConfirmation: data.zelleConfirmation,
                language: language,
              },
            });
            
            if (zelleNotifyError) {
              console.error('Error sending Zelle notification to admin:', zelleNotifyError);
            } else {
              console.log('Zelle payment notification sent to admin');
            }
          } catch (zelleError) {
            console.error('Error sending Zelle notification:', zelleError);
          }
        }
      }

      // Handle Stripe payment
      if (data.paymentMethod === 'Stripe') {
        const { data: sessionData, error: sessionError } = await supabase.functions.invoke(
          'create-booking-payment',
          {
            body: {
              bookingId: booking.booking_id,
              email: data.email,
              fullName: data.fullName,
              service: data.service,
              language: language,
            },
          }
        );

        if (sessionError) throw sessionError;

        // Redirect to Stripe Checkout
        if (sessionData?.url) {
          window.location.href = sessionData.url;
          return;
        }
      }

      // Clear saved form data
      localStorage.removeItem('bookingFormData');
      clearRateLimit('booking_form'); // Limpiar rate limit en envío exitoso
      
      // Differentiate success messages by payment method
      let successMessage = '';
      if (data.paymentMethod === 'Stripe') {
        successMessage = language === 'es' ? 'Redirigiendo al pago...' : 'Redirecting to payment...';
      } else if (data.paymentMethod === 'Zelle') {
        successMessage = language === 'es' 
          ? `Su reserva ha sido registrada con confirmación Zelle #${data.zelleConfirmation}. Verificaremos su pago y le enviaremos la confirmación de su cita.`
          : `Your booking has been registered with Zelle confirmation #${data.zelleConfirmation}. We will verify your payment and send you the appointment confirmation.`;
      } else {
        successMessage = language === 'es'
          ? 'Su reserva ha sido registrada. Recibirá un correo de confirmación una vez que procesemos su pago.'
          : 'Your booking has been registered. You will receive a confirmation email once we process your payment.';
      }
      
      toast({
        title: language === 'es' ? '¡Éxito!' : 'Success!',
        description: successMessage,
      });

      // Reset form for non-Stripe payments
      if (data.paymentMethod !== 'Stripe') {
        form.reset();
        setCurrentStep(1);
      }
      
    } catch (error: any) {
      // NO loguear datos sensibles del usuario
      console.error('Booking submission error:', error instanceof Error ? error.message : 'Unknown error');
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: language === 'es'
          ? 'Hubo un error al enviar su reserva. Por favor intente nuevamente.'
          : 'There was an error submitting your booking. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = async () => {
    let fieldsToValidate: any[] = [];
    
    switch (currentStep) {
      case 1:
        fieldsToValidate = ['service'];
        break;
      case 2:
        fieldsToValidate = ['fullName', 'dateOfBirth', 'email', 'phone', 'situation'];
        break;
      case 3:
        // Conditional fields - validation handled in ConditionalQuestionsStep
        break;
      case 4:
        fieldsToValidate = ['appointmentType', 'preferredDate', 'preferredTime'];
        break;
      case 5:
        fieldsToValidate = ['referredBy'];
        break;
      case 6:
        fieldsToValidate = ['paymentMethod'];
        // Additional validation for Zelle - requires BOTH confirmation AND receipt
        const paymentMethodVal = form.getValues('paymentMethod');
        if (paymentMethodVal === 'Zelle') {
          const zelleConfirmation = form.getValues('zelleConfirmation');
          const paymentEvidenceFile = form.getValues('paymentEvidenceFile');
          
          const hasConfirmation = zelleConfirmation && zelleConfirmation.trim().length > 0;
          const hasReceipt = !!paymentEvidenceFile;
          
          if (!hasConfirmation || !hasReceipt) {
            if (!hasConfirmation) {
              form.setError('zelleConfirmation', {
                type: 'manual',
                message: language === 'es' 
                  ? 'El número de confirmación de Zelle es obligatorio'
                  : 'Zelle confirmation number is required',
              });
            }
            
            toast({
              title: language === 'es' ? '⚠️ PROCESO INCOMPLETO' : '⚠️ INCOMPLETE PROCESS',
              description: language === 'es'
                ? 'Para continuar debe: 1) Completar el pago por Zelle, 2) Ingresar el número de confirmación, y 3) Subir el comprobante de pago.'
                : 'To continue you must: 1) Complete the Zelle payment, 2) Enter the confirmation number, and 3) Upload the payment receipt.',
              variant: 'destructive',
            });
            return;
          }
          fieldsToValidate.push('zelleConfirmation');
        }
        break;
      case 7:
        fieldsToValidate = ['consentNotifications', 'consentTerms'];
        break;
    }

    const isValid = await form.trigger(fieldsToValidate as any);
    
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
      setTimeout(() => {
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setTimeout(() => {
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const progress = (currentStep / totalSteps) * 100;

  return (
    <div ref={formTopRef}>
      <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-2xl">
          {t('booking.step')} {currentStep} {t('booking.of')} {totalSteps}
        </CardTitle>
        <CardDescription>
          <Progress value={progress} className="mt-2" />
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {currentStep === 1 && (
              <ServiceSelectionStep form={form} services={services} onSelect={scrollToNextButton} />
            )}
            
            {currentStep === 2 && (
              <BasicInfoStep form={form} />
            )}
            
            {currentStep === 3 && (
              <ConditionalQuestionsStep form={form} />
            )}
            
            {currentStep === 4 && (
              <AppointmentStep form={form} onSelect={scrollToNextButton} />
            )}
            
            {currentStep === 5 && (
              <ReferralStep form={form} onSelect={scrollToNextButton} />
            )}
            
            {currentStep === 6 && (
              <PaymentStep form={form} onZelleValidationChange={setIsZelleValid} onSelect={scrollToNextButton} />
            )}
            
            {currentStep === 7 && (
              <ConsentStep form={form} onSelect={scrollToNextButton} />
            )}

            {currentStep === 6 && (
              <div className="bg-destructive/20 border-2 border-destructive rounded-lg p-4 mt-2" role="alert" aria-live="polite">
                <p className="font-bold text-destructive text-base mb-1">
                  {language === 'es' ? '⚠️ Política de pago' : '⚠️ Payment Policy'}
                </p>
                <p className="text-destructive text-sm">
                  {language === 'es'
                    ? 'La tarifa de consulta de $150 no es reembolsable. Este pago asegura su cita.'
                    : 'The $150 consultation fee is non-refundable. This payment secures your appointment.'}
                </p>
              </div>
            )}

            <div className="flex justify-between pt-6 gap-4">
              {currentStep > 1 && (
                <Button type="button" variant="outline" onClick={prevStep} size="lg" className="min-h-[48px] px-6">
                  {t('booking.previous')}
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button 
                  ref={nextButtonRef}
                  type="button" 
                  onClick={nextStep} 
                  className="ml-auto min-h-[48px] px-8 text-base"
                  size="lg"
                  disabled={currentStep === 6 && form.watch('paymentMethod') === 'Zelle' && !isZelleValid}
                >
                  {currentStep === 6 && form.watch('paymentMethod') === 'Zelle' && !isZelleValid
                    ? (language === 'es' ? 'Complete el proceso Zelle' : 'Complete Zelle process')
                    : t('booking.next')
                  }
                </Button>
              ) : (
                <Button type="submit" disabled={isSubmitting} className="ml-auto min-h-[48px] px-8 text-base" size="lg">
                  {isSubmitting ? (
                    language === 'es' ? 'Enviando...' : 'Submitting...'
                  ) : (
                    t('booking.submit')
                  )}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  </div>
  );
};

export default BookingForm;
